import React from 'react';
import PropTypes from 'prop-types';
import { Button, Tooltip } from '@mui/material';
import { useHasPermission } from '../../hooks/usePermission';

/**
 * PermissionButton - Button that auto-disables without permission
 * 
 * Phase 5: M6.T6.2
 * 
 * Features:
 * - Button that automatically disables if user lacks permission
 * - Optional tooltip explaining why button is disabled
 * - Respects existing disabled prop
 * 
 * Usage Example:
 * 
 * <PermissionButton
 *   permission="users.delete"
 *   variant="contained"
 *   color="error"
 *   onClick={handleDelete}
 *   disabledTooltip="Kullanıcı silme yetkiniz yok"
 * >
 *   Sil
 * </PermissionButton>
 * 
 * Props:
 * - permission: Required permission code
 * - disabledTooltip: Message shown when disabled due to permission
 * - ...buttonProps: All standard Material-UI Button props
 */
const PermissionButton = ({
  permission,
  children,
  disabledTooltip = 'Bu işlem için yetkiniz yok',
  ...buttonProps
}) => {
  const hasPermission = useHasPermission(permission);
  
  // Button is disabled if:
  // 1. User lacks permission, OR
  // 2. Button is explicitly disabled via props
  const isDisabled = !hasPermission || buttonProps.disabled;
  
  const button = (
    <Button
      {...buttonProps}
      disabled={isDisabled}
    >
      {children}
    </Button>
  );
  
  // Wrap with tooltip if disabled due to permission
  // Note: Tooltip requires a span wrapper for disabled buttons
  if (!hasPermission) {
    return (
      <Tooltip title={disabledTooltip} arrow>
        <span>{button}</span>
      </Tooltip>
    );
  }
  
  // If has permission, return button without tooltip
  return button;
};

PermissionButton.propTypes = {
  // Required permission code (e.g., "users.delete")
  permission: PropTypes.string.isRequired,
  
  // Button content
  children: PropTypes.node.isRequired,
  
  // Tooltip message when disabled due to permission
  disabledTooltip: PropTypes.string,
  
  // Note: All other Button props are spread via ...buttonProps
};

export default PermissionButton;

